'use client';
import React, { useState, useEffect } from 'react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import Image from 'next/image';
import ScrollTopButton from '@/components/ScrollTopButton';
// Solutions Diagram Component
function SolutionsDiagram() {
  return (
    <div className="w-full max-w-6xl mx-auto px-4 py-8">
      {/* Desktop/Tablet Layout (lg screens and up) */}

      <div className="hidden lg:block">
        <div className="flex flex-col items-center gap-12">
          <div className="flex flex-col items-center">
            <div className="bg-gradient-to-br rounded-full p-10 bg-white">
              <div className="flex flex-col items-center gap-4">
                <Image
                  src="/icon/drone2.png"
                  alt="Drone"
                  width={200}
                  height={200}
                  className="object-contain"
                />
                <p className="font-bold text-gray-700 text-center">
                  TEXT TITLE
                </p>
              </div>
            </div>
            <div className="w-1 h-16 bg-gradient-to-b from-blue-400 to-transparent mt-4"></div>
          </div>

          {/* Bottom - Three Components (Equal Importance) */}
          <div className="grid grid-cols-3 gap-8 w-full">
            {/* VIS Camera */}
            <div className="flex flex-col items-center">
              <div className="bg-blue-50 rounded-full p-6 w-full">
                <div className="flex flex-col items-center gap-3">
                  <Image
                    src="/icon/camera3.png"
                    alt="VIS Camera"
                    width={150}
                    height={150}
                    className="object-contain"
                  />
                  <p className="text-sm font-semibold text-gray-700 text-center">
                    VIS Image
                  </p>
                </div>
              </div>
            </div>

            {/* IR Camera */}
            <div className="flex flex-col items-center">
              <div className="bg-blue-50 rounded-full p-6 w-full">
                <div className="flex flex-col items-center gap-3">
                  <Image
                    src="/icon/ircamera.png"
                    alt="IR Camera"
                    width={150}
                    height={150}
                    className="object-contain"
                  />
                  <p className="text-sm font-semibold text-gray-700 text-center">
                    IR Image
                  </p>
                </div>
              </div>
            </div>

            {/* AI Algorithm */}
            <div className="flex flex-col items-center">
              <div className="bg-blue-50 rounded-full p-6 w-full">
                <div className="flex flex-col items-center gap-3">
                  <Image
                    src="/icon/ai.png"
                    alt="AI Algorithm"
                    width={150}
                    height={150}
                    className="object-contain"
                  />
                  <p className="text-sm font-semibold text-gray-700 text-center">
                    AI Algorithm
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile/Tablet Layout (below lg screens) */}
      <div className="lg:hidden">
        <div className="flex flex-col items-center gap-6">
          {/* Drone (Most Important) */}
          <div className="flex flex-col items-center">
            <div className="bg-white rounded-full p-8 w-full max-w-xs">
              <div className="flex flex-col items-center gap-4">
                <Image
                  src="/icon/drone2.png"
                  alt="Drone"
                  width={140}
                  height={140}
                  className="object-contain"
                />
                <p className="text-base font-bold text-gray-700 text-center">
                  TEXT TITLE
                </p>
              </div>
            </div>
            <div className="w-1 h-12 bg-gradient-to-b from-blue-400 to-transparent"></div>
          </div>

          {/* Three Components (Equal Importance) */}
          <div className="flex flex-col gap-4 w-full max-w-md">
            {/* VIS Camera */}
            <div className="bg-blue-50 rounded-full p-5 w-full">
              <div className="flex items-center gap-4">
                <Image
                  src="/icon/camera3.png"
                  alt="VIS Camera"
                  width={80}
                  height={80}
                  className="object-contain"
                />
                <p className="text-sm font-semibold text-gray-700">VIS Image</p>
              </div>
            </div>

            {/* IR Camera */}
            <div className="bg-blue-50 rounded-full p-5 w-full">
              <div className="flex items-center gap-4">
                <Image
                  src="/icon/ircamera.png"
                  alt="IR Camera"
                  width={80}
                  height={80}
                  className="object-contain"
                />
                <p className="text-sm font-semibold text-gray-700">IR Image</p>
              </div>
            </div>

            {/* AI Algorithm */}
            <div className="bg-blue-50 rounded-full p-5 w-full">
              <div className="flex items-center gap-4">
                <Image
                  src="/icon/ai.png"
                  alt="AI Algorithm"
                  width={80}
                  height={80}
                  className="object-contain"
                />
                <p className="text-sm font-semibold text-gray-700">
                  AI Algorithm
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function page() {
  const [isVisible, setIsVisible] = useState(false);
  const [hoveredTech, setHoveredTech] = useState(null);
  const [visibleIcons, setVisibleIcons] = useState(new Set());
  const [textVisible, setTextVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const setupObserver = () => {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              const index = parseInt(entry.target.getAttribute('data-index'));
              setVisibleIcons((prev) => new Set([...prev, index]));
            }
          });
        },
        { threshold: 0.2, rootMargin: '-50px' },
      );

      const textObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              setTextVisible(true);
            }
          });
        },
        { threshold: 0.3 },
      );

      const iconElements = document.querySelectorAll('.icon-item');
      iconElements.forEach((el) => observer.observe(el));

      const textElement = document.querySelector('.text-section');
      if (textElement) {
        textObserver.observe(textElement);
      }

      return () => {
        observer.disconnect();
        textObserver.disconnect();
      };
    };

    const timeoutId = setTimeout(setupObserver, 100);

    return () => clearTimeout(timeoutId);
  }, []);

  return (
    <div className="font-sans min-h-screen">
      <Header />
      <ScrollTopButton />
      <section className="pt-32 pb-20 lg:pt-40 lg:pb-24">
        <div className="px-4 sm:px-6 lg:px-44">
          {/* 장식 요소 */}
          <div
            className={`flex items-center gap-4 mb-6 transition-all duration-700 ${
              isVisible
                ? 'opacity-100 translate-y-0'
                : 'opacity-0 -translate-y-4'
            }`}
          >
            <div className="w-20 h-1 bg-blue-600"></div>
            <span className="text-blue-600 font-semibold text-sm uppercase tracking-wider">
              Core Technologies
            </span>
          </div>

          {/* 메인 제목 */}
          <h2
            className={`text-5xl md:text-7xl font-black text-gray-800 mb-6 transition-all duration-700 delay-150 ${
              isVisible
                ? 'opacity-100 translate-y-0'
                : 'opacity-0 -translate-y-4'
            }`}
          >
            Key Technologies
          </h2>

          {/* 설명 텍스트 */}
          <p
            className={`text-base md:text-xl text-gray-600 max-w-3xl leading-relaxed transition-all duration-700 delay-300 ${
              isVisible
                ? 'opacity-100 translate-y-0'
                : 'opacity-0 -translate-y-4'
            }`}
          >
            무인기 플랫폼부터 AI 알고리즘까지, KD darts만의 차별화된 기술력.
            <br />
            지속적인 연구개발로 검증된 첨단 드론 기술을 소개합니다.
          </p>
        </div>
      </section>
      <main className="flex flex-col text-gray-800">
        {/* <section className="pb-20 ">
          <section className="px-4 mx-auto pt-14 bg-bgColor sm:px-6 lg:pt-10 lg:px-44 bg-gray-50">
            <div className="flex flex-col items-center justify-around pt-10 lg:flex-row ">
              <SolutionsDiagram />
            </div>
          </section>
        </section> */}

        <section className="bg-gray-950 py-20 lg:py-32">
          <div className="px-4 sm:px-6 lg:px-44">
            {/* 제목 */}
            <h2 className="text-3xl md:text-5xl lg:text-6xl font-black text-white mb-4 leading-tight">
              <span className="text-blue-600">KDdarts 기술</span>은 체계적인
              <br className="hidden md:block" />
              탐지·획득·식별·추적 프로세스를 통해 구현됩니다.
            </h2>

            {/* 프로세스 리스트 */}
            <ul className="mt-20 ">
              {/* 01 - Detection */}
              <li className="border-y border-white py-10 ">
                <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-end">
                  <div className="order-2 lg:order-1 pb-4">
                    <span className="text-3xl lg:text-3xl font-black text-gray-500 mb-4 block">
                      01
                    </span>
                    <div className="space-y-4">
                      <h3 className="text-4xl lg:text-4xl font-bold text-white">
                        Detection (탐지)
                      </h3>
                      <p className="text-base lg:text-lg text-gray-300 leading-relaxed">
                        VIS(가시광) 및 IR(열영상) 센서를 활용하여 관심 영역 내의
                        모든 움직이는 객체를 실시간으로 탐지합니다. 다중 센서
                        융합 기술로 주·야간 환경에서도 높은 탐지율을 보장합니다.
                      </p>
                    </div>
                  </div>
                  <figure className="order-1 lg:order-2 p-8 lg:p-12 flex items-center justify-center min-h-[300px]">
                    <svg
                      width="360"
                      height="360"
                      viewBox="0 0 360 360"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      className="w-full h-auto max-w-[360px]"
                    >
                      <circle
                        cx="180"
                        cy="180"
                        r="179"
                        stroke="white"
                        strokeWidth="2"
                        fill="none"
                      ></circle>
                      <g id="orb107" transform="rotate(0 180 180)">
                        <animateTransform
                          attributeName="transform"
                          type="rotate"
                          from="0 180 180"
                          to="360 180 180"
                          dur="18s"
                          repeatCount="indefinite"
                        ></animateTransform>
                        <g transform="translate(180,180)">
                          <g transform="translate(72,0)">
                            <circle
                              r="107"
                              cx="0"
                              cy="0"
                              stroke="white"
                              strokeWidth="2"
                              fill="none"
                            ></circle>
                            <g id="orb59" transform="rotate(0)">
                              <animateTransform
                                attributeName="transform"
                                type="rotate"
                                from="0"
                                to="360"
                                dur="12s"
                                repeatCount="indefinite"
                              ></animateTransform>
                              <g transform="translate(48,0)">
                                <circle
                                  r="59"
                                  cx="0"
                                  cy="0"
                                  stroke="white"
                                  strokeWidth="2"
                                  fill="none"
                                ></circle>
                                <g id="orb29" transform="rotate(0)">
                                  <animateTransform
                                    attributeName="transform"
                                    type="rotate"
                                    from="0"
                                    to="360"
                                    dur="8s"
                                    repeatCount="indefinite"
                                  ></animateTransform>
                                  <g transform="translate(30,0)">
                                    <circle
                                      r="29"
                                      cx="0"
                                      cy="0"
                                      stroke="#3B82F6"
                                      strokeWidth="2"
                                      fill="none"
                                    ></circle>
                                  </g>
                                </g>
                              </g>
                            </g>
                          </g>
                        </g>
                      </g>
                    </svg>
                  </figure>
                </div>
              </li>

              {/* 02 - Acquisition */}
              <li className="border-b border-white py-10 ">
                <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-end">
                  <div className="order-2 pb-4">
                    <span className="text-3xl lg:text-3xl font-black text-gray-500 mb-4 block">
                      02
                    </span>
                    <div className="space-y-4">
                      <h3 className="text-2xl lg:text-3xl font-bold text-white">
                        Acquisition (획득)
                      </h3>
                      <p className="text-base lg:text-lg text-gray-300 leading-relaxed">
                        탐지된 객체의 위치·속도·방향 정보를 정밀하게 획득하고,
                        무인기에게 정보를 공유합니다.
                      </p>
                    </div>
                  </div>
                  <figure className="order-1  p-8 lg:p-12 flex items-center justify-center min-h-[300px]">
                    <svg
                      width="360"
                      height="360"
                      viewBox="0 0 360 360"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      className="w-full h-auto max-w-[360px]"
                    >
                      <g>
                        <g transform="matrix(0.866025 0.5 -0.866025 0.5 181.445 160.6)">
                          <rect
                            x="-7.15256e-08"
                            y="1.2"
                            width="197.6"
                            height="197.6"
                            stroke="white"
                            strokeWidth="2.4"
                            fill="none"
                          ></rect>
                        </g>
                        <animateTransform
                          attributeName="transform"
                          type="translate"
                          values="0 0; 0 -40; 0 0"
                          keyTimes="0; .5; 1"
                          dur="4s"
                          repeatCount="indefinite"
                          calcMode="spline"
                          keySplines="0.4 0 0.2 1; 0.4 0 0.2 1"
                        ></animateTransform>
                      </g>
                      <g>
                        <g transform="matrix(0.866025 0.5 -0.866025 0.5 181.445 120.6)">
                          <rect
                            x="-7.15256e-08"
                            y="1.2"
                            width="197.6"
                            height="197.6"
                            stroke="#3B82F6"
                            strokeWidth="2.4"
                            fill="none"
                          ></rect>
                        </g>
                        <animateTransform
                          attributeName="transform"
                          type="translate"
                          values="0 0; 0 0; 0 0"
                          keyTimes="0; .5; 1"
                          dur="4s"
                          repeatCount="indefinite"
                          calcMode="spline"
                          keySplines="0.4 0 0.2 1; 0.4 0 0.2 1"
                        ></animateTransform>
                      </g>
                      <g>
                        <g transform="matrix(0.866025 0.5 -0.866025 0.5 181.445 80.6)">
                          <rect
                            x="-7.15256e-08"
                            y="1.2"
                            width="197.6"
                            height="197.6"
                            stroke="white"
                            strokeWidth="2.4"
                            fill="none"
                          ></rect>
                        </g>
                        <animateTransform
                          attributeName="transform"
                          type="translate"
                          values="0 0; 0 40; 0 0"
                          keyTimes="0; .5; 1"
                          dur="4s"
                          repeatCount="indefinite"
                          calcMode="spline"
                          keySplines="0.4 0 0.2 1; 0.4 0 0.2 1"
                        ></animateTransform>
                      </g>
                      <g>
                        <g transform="matrix(0.866025 0.5 -0.866025 0.5 181.445 40.6)">
                          <rect
                            x="-7.15256e-08"
                            y="1.2"
                            width="197.6"
                            height="197.6"
                            stroke="#3B82F6"
                            strokeWidth="2.4"
                            fill="none"
                          ></rect>
                        </g>
                        <animateTransform
                          attributeName="transform"
                          type="translate"
                          values="0 0; 0 80; 0 0"
                          keyTimes="0; .5; 1"
                          dur="4s"
                          repeatCount="indefinite"
                          calcMode="spline"
                          keySplines="0.4 0 0.2 1; 0.4 0 0.2 1"
                        ></animateTransform>
                      </g>
                      <g>
                        <g transform="matrix(0.866025 0.5 -0.866025 0.5 181.445 0.6)">
                          <rect
                            x="-7.15256e-08"
                            y="1.2"
                            width="197.6"
                            height="197.6"
                            stroke="white"
                            strokeWidth="2.4"
                            fill="none"
                          ></rect>
                        </g>
                        <animateTransform
                          attributeName="transform"
                          type="translate"
                          values="0 0; 0 120; 0 0"
                          keyTimes="0; .5; 1"
                          dur="4s"
                          repeatCount="indefinite"
                          calcMode="spline"
                          keySplines="0.4 0 0.2 1; 0.4 0 0.2 1"
                        ></animateTransform>
                      </g>
                    </svg>
                  </figure>
                </div>
              </li>

              {/* 03 - Recognition */}
              <li className="border-b border-white py-10">
                <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-end">
                  <div className="order-2 lg:order-1 pb-4">
                    <span className="text-3xl lg:text-3xl font-black text-gray-500 mb-4 block">
                      03
                    </span>
                    <div className="space-y-4">
                      <h3 className="text-2xl lg:text-3xl font-bold text-white">
                        Recognition (식별)
                      </h3>
                      <p className="text-base lg:text-lg text-gray-300 leading-relaxed">
                        AI 딥러닝 알고리즘으로 표적의 종류(차량, 사람, 드론
                        등)를 자동으로 분류하고, 형태·크기·움직임 패턴을
                        분석하여 위협 수준을 판단합니다. 실시간 학습으로 식별
                        정확도가 지속적으로 향상됩니다.
                      </p>
                    </div>
                  </div>
                  <figure className="order-1 lg:order-2  p-8 lg:p-12 flex items-center justify-center min-h-[300px]">
                    <svg
                      width="360"
                      height="360"
                      viewBox="0 0 360 360"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      className="w-full h-auto max-w-[360px]"
                    >
                      <defs>
                        <style>{`
                  .ring { stroke:#fff; stroke-width:1.2; fill:none; vector-effect:non-scaling-stroke; stroke-linecap:round; }
                  .dot  { stroke:#3B82F6; stroke-width:1.2; fill:none; vector-effect:non-scaling-stroke; }
                  .front{ stroke-opacity:1; }
                  .back { stroke-opacity:1; }
                `}</style>
                        <path
                          id="meridian"
                          className="ring"
                          pathLength="100"
                          d="M180 3.6001C204.162 3.6001 226.35 22.9546 242.569 54.9604C258.754 86.8989 268.8 131.105 268.8 180C268.8 228.894 258.754 273.101 242.569 305.04C226.35 337.045 204.162 356.4 180 356.4C155.838 356.4 133.65 337.045 117.431 305.04C101.246 273.101 91.2 228.894 91.2 180C91.2 131.105 101.246 86.8989 117.431 54.9604C133.65 22.9546 155.838 3.6001 180 3.6001Z"
                        ></path>
                        <path
                          id="equator"
                          className="ring"
                          pathLength="100"
                          d="M3.60059 180C3.60059 155.838 22.9551 133.65 54.9609 117.431C86.8994 101.246 131.106 91.2002 180 91.2002C228.895 91.2002 273.102 101.246 305.04 117.431C337.046 133.65 356.4 155.838 356.4 180C356.4 204.162 337.046 226.35 305.04 242.569C273.102 258.754 228.895 268.8 180 268.8C131.106 268.8 86.8994 258.754 54.9609 242.569C22.9551 226.35 3.60059 204.162 3.60059 180Z"
                        ></path>
                      </defs>
                      <circle
                        cx="180"
                        cy="180"
                        r="178.8"
                        className="ring"
                      ></circle>
                      <circle
                        cx="180"
                        cy="180"
                        r="14.4"
                        className="dot"
                      ></circle>
                      <g>
                        <animateTransform
                          attributeName="transform"
                          type="rotate"
                          from="0 180 180"
                          to="360 180 180"
                          dur="6s"
                          repeatCount="indefinite"
                        ></animateTransform>
                        <use
                          href="#meridian"
                          className="back"
                          strokeDasharray="50 50"
                          strokeDashoffset="50"
                        ></use>
                        <use
                          href="#meridian"
                          className="front"
                          strokeDasharray="50 50"
                          strokeDashoffset="0"
                        ></use>
                      </g>
                      <g>
                        <animateTransform
                          attributeName="transform"
                          type="rotate"
                          from="0 180 180"
                          to="-360 180 180"
                          dur="6s"
                          repeatCount="indefinite"
                        ></animateTransform>
                        <use
                          href="#equator"
                          className="back"
                          strokeDasharray="50 50"
                          strokeDashoffset="50"
                        ></use>
                        <use
                          href="#equator"
                          className="front"
                          strokeDasharray="50 50"
                          strokeDashoffset="0"
                        ></use>
                      </g>
                    </svg>
                  </figure>
                </div>
              </li>

              {/* 04 - Tracking */}
              <li className="border-b border-white py-10">
                <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-end">
                  <div className="order-2 pb-4">
                    <span className="text-3xl lg:text-3xl font-black text-gray-500 mb-4 block">
                      04
                    </span>
                    <div className="space-y-4">
                      <h3 className="text-2xl lg:text-3xl font-bold text-white">
                        Tracking (추적)
                      </h3>
                      <p className="text-base lg:text-lg text-gray-300 leading-relaxed">
                        식별된 표적을 연속적으로 추적하며, 순간적인 가림이나
                        센서 전환 시에도 추적을 유지합니다. 예측 알고리즘으로
                        표적의 다음 위치를 예측하여, 빠르게 움직이는 물체도
                        안정적으로 따라갑니다.
                      </p>
                    </div>
                  </div>
                  <figure className="order-1  p-8 lg:p-12 flex items-center justify-center min-h-[300px]">
                    <svg
                      width="360"
                      height="360"
                      viewBox="0 0 360 360"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      className="w-full h-auto max-w-[360px]"
                    >
                      <style>{`
                .spin{
                  transform-box: fill-box;
                  transform-origin: center;
                  animation: spin 10s linear infinite;
                  will-change: transform;
                }
                .spin-rev{ animation-direction: reverse; }
                @keyframes spin { to { transform: rotate(360deg); } }
                @media (prefers-reduced-motion: reduce){
                  .spin{ animation: none; }
                }
              `}</style>
                      <g transform="rotate(45 180 1.41421)">
                        <g className="spin">
                          <rect
                            x="180"
                            y="1.41421"
                            width="252.558"
                            height="252.558"
                            stroke="white"
                            strokeWidth="2"
                            fill="none"
                          ></rect>
                        </g>
                      </g>
                      <g transform="rotate(90 267.6 92.4004)">
                        <g className="spin spin-rev">
                          <rect
                            x="267.6"
                            y="92.4004"
                            width="175.199"
                            height="175.199"
                            stroke="white"
                            strokeWidth="2"
                            fill="none"
                          ></rect>
                        </g>
                      </g>
                      <g transform="rotate(45 180 95.0143)">
                        <g className="spin">
                          <rect
                            x="180"
                            y="95.0143"
                            width="120.187"
                            height="120.187"
                            stroke="white"
                            strokeWidth="2"
                            fill="none"
                          ></rect>
                        </g>
                      </g>
                      <g transform="rotate(90 219.6 140.4)">
                        <g className="spin spin-rev">
                          <rect
                            x="219.6"
                            y="140.4"
                            width="79.1998"
                            height="79.1998"
                            stroke="#3B82F6"
                            strokeWidth="2"
                            fill="none"
                          ></rect>
                        </g>
                      </g>
                    </svg>
                  </figure>
                </div>
              </li>
            </ul>
          </div>
        </section>
        <section className="relative w-full min-h-screen overflow-hidden">
          <div
            className="absolute inset-0 transition-all duration-500 ease-in-out"
            style={{
              backgroundImage: `url(${
                hoveredTech ? hoveredTech.bgImage : '/images/technologys/1.png'
              })`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/10 to-black/30" />

          <div className="relative z-10 flex flex-col justify-center min-h-screen py-20">
            <h2 className="px-4 mb-16 text-6xl font-black text-white sm:px-6 lg:px-44">
              Key Technologies
            </h2>

            {/* Desktop Layout - Horizontal */}
            <div className="hidden lg:flex justify-start gap-0 px-4 sm:px-6 lg:px-44 mt-40">
              {ICONIMAGE.map((image, index) => {
                const isHovered = hoveredTech?.id === image.id;
                return (
                  <div
                    key={image.title}
                    className={`flex flex-col items-center justify-center w-60 h-68 cursor-pointer transition-all mt-40 duration-300 ${
                      index !== 0 ? 'border-l border-white/20' : ''
                    } ${
                      isHovered
                        ? 'bg-blue-700/40 backdrop-blur-md  '
                        : 'bg-black/20 hover:bg-black/50'
                    }`}
                    onMouseEnter={() => setHoveredTech(image)}
                  >
                    <div className="flex flex-col items-center gap-8 ">
                      <Image
                        src={image.imageSrc}
                        alt={image.title}
                        width={100}
                        height={100}
                        className="object-contain"
                      />
                      <h4 className="px-6 tex-lg font-bold text-center text-white leading-tight">
                        {image.title}
                      </h4>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Mobile/Tablet Layout - Grid */}
            <div className="lg:hidden grid grid-cols-2 md:grid-cols-3 gap-4 px-4 sm:px-6 mt-20">
              {ICONIMAGE.map((image) => {
                const isHovered = hoveredTech?.id === image.id;
                return (
                  <div
                    key={image.title}
                    className={`flex flex-col items-center justify-center p-6 rounded-2xl cursor-pointer transition-all duration-300 ${
                      isHovered
                        ? 'bg-blue-700/40 backdrop-blur-md '
                        : 'bg-black/20 hover:bg-black/50'
                    }`}
                    onMouseEnter={() => setHoveredTech(image)}
                    onClick={() => setHoveredTech(image)}
                  >
                    <div className="flex flex-col items-center gap-4">
                      <Image
                        src={image.imageSrc}
                        alt={image.title}
                        width={80}
                        height={80}
                        className="object-contain"
                      />
                      <h4 className="text-sm font-bold text-center text-white leading-tight">
                        {image.title}
                      </h4>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
        <section
          className="relative bg-gray-950 py-16 lg:py-24"
          style={{
            backgroundImage: "url('/images/bg1.png')",
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        >
          <div className="absolute inset-0 bg-black/40 z-0"></div>
          <div className="relative z-10 px-4 sm:px-6 lg:px-44 mx-auto">
            {/* 상단 텍스트 영역 */}
            <div className="flex flex-col gap-4 mb-10">
              <div>
                <span className="text-xs font-semibold tracking-[0.2em] text-blue-400 uppercase">
                  Multi-Sensor Tracking
                </span>
                <h3 className="text-3xl md:text-7xl font-black text-white">
                  VIS / IR
                </h3>
              </div>
              <div>
                <p className="text-sm md:text-lg text-gray-300 max-w-2xl leading-relaxed">
                  주·야간 환경에서 동일 표적을 VIS(가시광)와 IR(열영상)로 동시에
                  추적하여 탐지·획득·식별·추적 성능을 극대화합니다. 복잡한 교통
                  상황에서도 관심 표적을 안정적으로 따라갑니다.
                </p>
              </div>
            </div>

            {/* 이미지 + 오버레이 카드 */}
            <div className="relative w-full mx-auto overflow-hidden border border-white/10 shadow-2xl">
              <Image
                src="/images/trackingtarget.png"
                alt="VIS / IR Tracking Target"
                width={1920}
                height={150}
                className="w-full h-auto object-cover"
                priority
              />

              {/* 좌측 VIS 라벨 */}
              <div className="absolute top-2 left-2 md:top-4 md:left-4">
                <div className="inline-flex items-center gap-1.5 md:gap-2 rounded-full bg-black/70 px-2 py-0.5 md:px-4 md:py-1 backdrop-blur-sm">
                  <span className="w-1.5 h-1.5 md:w-2 md:h-2 rounded-full bg-emerald-400 animate-pulse" />
                  <span className="text-[10px] md:text-sm font-semibold text-emerald-300">
                    VIS 영상 기반 추적
                  </span>
                </div>
              </div>

              {/* 우측 IR 라벨 */}
              <div className="absolute top-2 right-2 md:top-4 md:right-4">
                <div className="inline-flex items-center gap-1.5 md:gap-2 rounded-full bg-black/70 px-2 py-0.5 md:px-4 md:py-1 backdrop-blur-sm">
                  <span className="w-1.5 h-1.5 md:w-2 md:h-2 rounded-full bg-red-400 animate-pulse" />
                  <span className="text-[10px] md:text-sm font-semibold text-red-300">
                    IR 영상 기반 추적
                  </span>
                </div>
              </div>

              {/* 데스크톱: 하단 설명 배지 (이미지 위) */}
              <div className="hidden md:block absolute inset-x-0 bottom-0">
                <div className="flex flex-col md:flex-row items-center justify-between gap-3 bg-gradient-to-t from-black/80 via-black/60 to-transparent px-6 py-4">
                  <p className="text-sm md:text-base text-gray-100">
                    동일 표적 좌표 기반으로 VIS·IR 센서가 연동되어, 센서 전환
                    시에도 추적이 끊기지 않습니다.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <span className="px-3 py-1 text-sm md:text-base font-semibold rounded-full bg-white/10 text-gray-100 border border-white/10">
                      실시간 표적 재획득
                    </span>
                    <span className="px-3 py-1 text-sm md:text-base font-semibold rounded-full bg-white/10 text-gray-100 border border-white/10">
                      야간/악천후 대응
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* 모바일: 설명 부분 (이미지 아래) */}
            <div className="md:hidden mt-6 space-y-4">
              <p className="text-sm text-gray-300 leading-relaxed">
                동일 표적 좌표 기반으로 VIS·IR 센서가 연동되어, 센서 전환 시에도
                추적이 끊기지 않습니다.
              </p>
              <div className="flex flex-wrap gap-2">
                <span className="px-3 py-1.5 text-xs font-semibold rounded-full bg-white/10 text-gray-100 border border-white/10">
                  실시간 표적 재획득
                </span>
                <span className="px-3 py-1.5 text-xs font-semibold rounded-full bg-white/10 text-gray-100 border border-white/10">
                  야간/악천후 대응
                </span>
              </div>
            </div>
          </div>
        </section>
        <section className="relative pt-24 pb-20 mx-auto sm:px-6 lg:px-8 lg:pt-40 w-full overflow-hidden">
          {/* 배경 영상 */}
          <video
            className="absolute top-0 left-0 w-full h-full object-cover z-0"
            autoPlay
            muted
            loop
            playsInline
          >
            <source src="/video/intro8.mov" type="video/mp4" />
            Your browser does not support the video tag.
          </video>

          {/* 반투명 오버레이 */}
          <div className="absolute inset-0 bg-black/50 z-10"></div>

          {/* 콘텐츠 영역 */}
          <div className="relative z-20 flex justify-center">
            <div className="w-full mb-4 lg:grid lg:grid-flow-col lg:grid-rows-2">
              {ICONIMAGE_2.map((image, index) => {
                const isIconVisible = visibleIcons.has(index);
                return (
                  <div
                    key={image.title}
                    data-index={index}
                    className={`icon-item mb-10 transition-all duration-700 ease-out ${
                      isIconVisible
                        ? 'opacity-100 translate-y-0 scale-100'
                        : 'opacity-0 translate-y-16 scale-95'
                    }`}
                    style={{
                      transitionDelay: isIconVisible
                        ? `${index * 150}ms`
                        : '0ms',
                    }}
                  >
                    <div className="flex flex-col items-center">
                      <div className="p-6 bg-black/70 rounded-full w-46 transform transition-transform hover:scale-110 duration-300">
                        <Image
                          src={image.imageSrc}
                          alt={image.title}
                          width="0"
                          height="0"
                          sizes="100vw"
                          style={{ width: image.width, height: 'auto' }}
                          className={image.className}
                        />
                      </div>
                      <div className="pt-3 text-center ">
                        <p className="text-sm text-white line-clamp-3">
                          {`${image.title}`}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* 텍스트 섹션 */}
          <div
            className={`text-section relative z-20 px-4 mt-96 lg:px-44 transition-all duration-1000 ease-out ${
              textVisible
                ? 'opacity-100 translate-y-0'
                : 'opacity-0 translate-y-10'
            }`}
          >
            <h2 className="mb-10 text-5xl font-black text-gray-100">
              KD darts
            </h2>
            <p className="font-normal text-gray-100">
              Detection Acquisition Recognition Tracking & Drone AI Robotics
              Technology Solution
            </p>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}

const ICONIMAGE_2 = [
  {
    id: 1,
    imageSrc: '/icon/tech-7.svg',
    title: `중복 표적 지정 방식 알고리즘`,
    width: 100,
    height: 50,
    className: ' right-0 m-auto ml-auto mr-auto text-center left-4 bottom-3',
  },
  {
    id: 2,
    imageSrc: '/icon/tech-8.svg',
    title: '추적 유지 기능',
    width: 100,
    height: 50,
    className: ' right-0 m-auto ml-auto mr-auto text-center left-4 bottom-3',
  },
  {
    id: 3,
    imageSrc: '/icon/tech-9.svg',
    title: '변환하는 물체의 실시간 최적 특징 조합 적출 기술',
    width: 100,
    height: 50,
    className: ' right-0 m-auto ml-auto mr-auto text-center left-4 bottom-3',
  },
  {
    id: 4,
    imageSrc: '/icon/tech-10.svg',
    title: '형태 강인성 기반 다수 물체 탐지 기술',
    width: 100,
    height: 50,
    className: ' right-0 m-auto ml-auto mr-auto text-center left-4 bottom-3',
  },
  {
    id: 5,
    imageSrc: '/icon/tech-11.svg',
    title: '순간적 영상 위치 변화 보정 기술',
    width: 100,
    height: 50,
    className: ' right-0 m-auto ml-auto mr-auto text-center left-4 bottom-3',
  },
];

const ICONIMAGE = [
  {
    id: 1,
    imageSrc: '/icon/tech-1.svg',
    title: '무인기 플랫폼 설계 및 제작 기술',
    width: 150,
    height: 50,
    className: ' right-0 m-auto ml-auto mr-auto text-center left-4 bottom-3',
    bgImage: '/images/technologys/1.png',
  },
  {
    id: 2,
    imageSrc: '/icon/tech-2.svg',
    title: '김벌 설계 및 제작 기술',
    width: 150,
    height: 50,
    className: ' right-0 m-auto ml-auto mr-auto text-center left-4 bottom-3',
    bgImage: '/images/technologys/2.png',
  },
  {
    id: 3,
    imageSrc: '/icon/tech-3.svg',
    title: '표적 자동인식 기술',
    width: 150,
    height: 50,
    className: ' right-0 m-auto ml-auto mr-auto text-center left-4 bottom-3',
    bgImage: '/images/technologys/3.png',
  },
  {
    id: 4,
    imageSrc: '/icon/tech-4.svg',
    title: '무인기 항법 시스템 설계 기술',
    width: 150,
    height: 50,
    className: ' right-0 m-auto ml-auto mr-auto text-center left-4 bottom-3',
    bgImage: '/images/technologys/4.png',
  },
  {
    id: 5,
    imageSrc: '/icon/tech-5.svg',
    title: '자율 유도비행 제어 기술',
    width: 150,
    height: 50,
    className: ' right-0 m-auto ml-auto mr-auto text-center left-4 bottom-3',
    bgImage: '/images/technologys/5.png',
  },
  {
    id: 6,
    imageSrc: '/icon/tech-6.svg',
    title: '군집 비행 제어 기술',
    width: 150,
    height: 50,
    className: ' right-0 m-auto ml-auto mr-auto text-center left-4 bottom-3',
    bgImage: '/images/technologys/6.png',
  },
];
